import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Mail, CheckCircle, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { LogoIcon } from '../components/Logo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error, resend
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { setAuthToken } = useAuth();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const pendingEmail = searchParams.get('email');
  const userType = searchParams.get('type') || 'user'; // user or msp

  useEffect(() => {
    const verify = async () => {
      if (!token) return;
      
      try {
        const endpoint = userType === 'msp' 
          ? `${API}/api/msp/verify-email`
          : `${API}/api/auth/verify-email`;
        
        const response = await axios.post(endpoint, { token });
        
        setStatus('success');
        setMessage('Your email has been verified successfully!');
        
        // Store the token and redirect to appropriate location
        if (response.data.access_token) {
          setAuthToken(response.data.access_token);
          
          // Store in sessionStorage for onboarding flow
          sessionStorage.setItem('onboarding_token', response.data.access_token);
          
          toast.success('Email verified! Redirecting to setup...');
          
          // Redirect after a short delay
          setTimeout(() => {
            if (userType === 'msp') {
              navigate('/msp/dashboard');
            } else {
              navigate('/onboarding?step=1');
            }
          }, 2000);
        }
      } catch (error) {
        setStatus('error');
        const errorDetail = error.response?.data?.detail || 'Verification failed';
        setMessage(errorDetail);
      }
    };
    
    if (token) {
      verify();
    } else if (pendingEmail) {
      setEmail(pendingEmail);
      setStatus('resend');
    } else {
      setStatus('resend');
    }
  }, [token, pendingEmail, userType, setAuthToken, navigate]);

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResendLoading(true);
    try {
      const endpoint = userType === 'msp'
        ? `${API}/api/msp/resend-verification`
        : `${API}/api/auth/resend-verification`;
      
      await axios.post(endpoint, { email });
      toast.success('Verification email sent! Please check your inbox.');
      setMessage('A new verification email has been sent. Please check your inbox and spam folder.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <LogoIcon size="xl" />
          </div>
          <CardTitle className="text-2xl font-heading text-white">
            {status === 'loading' && 'Verifying Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
            {status === 'resend' && 'Verify Your Email'}
          </CardTitle>
          <CardDescription className="text-slate-400">
            {status === 'loading' && 'Please wait while we verify your email address.'}
            {status === 'success' && 'Your account is now active.'}
            {status === 'error' && 'We could not verify your email.'}
            {status === 'resend' && 'Check your inbox for the verification link.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-slate-400">Verifying your email address...</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <p className="text-slate-300 text-center mb-4">{message}</p>
              <p className="text-slate-400 text-sm text-center">
                Redirecting you to complete your setup...
              </p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <p className="text-slate-300 text-center mb-6">{message}</p>
              
              <div className="w-full space-y-4">
                <p className="text-slate-400 text-sm text-center">
                  Need a new verification link?
                </p>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <Button
                  onClick={handleResendVerification}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={resendLoading}
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Resend/Pending State */}
          {status === 'resend' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mb-4">
                <Mail className="w-10 h-10 text-blue-500" />
              </div>
              <p className="text-slate-300 text-center mb-2">
                We&apos;ve sent a verification link to your email.
              </p>
              <p className="text-slate-400 text-sm text-center mb-6">
                Please check your inbox and spam folder.
              </p>
              
              <div className="w-full space-y-4">
                <p className="text-slate-400 text-sm text-center">
                  Didn&apos;t receive the email?
                </p>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <Button
                  onClick={handleResendVerification}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={resendLoading}
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Back to Login Link */}
          <div className="text-center pt-4 border-t border-slate-800">
            <Link 
              to={userType === 'msp' ? '/msp/login' : '/login'} 
              className="text-slate-400 hover:text-white text-sm"
            >
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
